/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import { useAppSelector } from '@redux/hooks'
import { Dispatch, SetStateAction, useEffect } from 'react'
import { AccountFieldManager, AuthFieldType } from '../components'
import { AccountMetadataType, AccountType } from '../types'

const FlexRadioInline = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  margin-top: 10px;
  margin-bottom: 10px;
`

interface AdobeProps {
  updatedAccount: AccountType | AccountMetadataType
  setUpdatedAccount: Dispatch<SetStateAction<AccountType>>
  setRequiredFieldMissing: Dispatch<SetStateAction<boolean>>
  newConnection: boolean
}

export const Adobe = ({
  updatedAccount,
  setUpdatedAccount,
  setRequiredFieldMissing,
  newConnection,
}: AdobeProps) => {
  useEffect(() => {
    if (
      (!(updatedAccount as AccountType)?.metadata?.company &&
        !!(updatedAccount as AccountMetadataType).company) ||
      (!(updatedAccount as AccountType)?.metadata?.username &&
        !!(updatedAccount as AccountMetadataType)?.username)
    ) {
      setUpdatedAccount(prevState => ({
        ...prevState,
        metadata: {
          ...prevState.metadata,
          company:
            (updatedAccount as AccountType)?.metadata?.company ??
            (updatedAccount as AccountMetadataType).company,
          username:
            (updatedAccount as AccountType)?.metadata?.username ??
            (updatedAccount as AccountMetadataType).username,
        },
      }))
    }
  }, [
    (updatedAccount as AccountType)?.metadata?.company,
    (updatedAccount as AccountType)?.metadata?.username,
    (updatedAccount as AccountMetadataType)?.company,
    (updatedAccount as AccountMetadataType)?.username,
    setUpdatedAccount,
  ])

  const AEMFields: AuthFieldType[] =
    !(updatedAccount as AccountType).metadata.auth_type ||
    (updatedAccount as AccountType).metadata.auth_type === 'fields'
      ? [
          {
            fieldValue: (updatedAccount as AccountType)?.metadata?.company,
            fieldKey: 'company',
            fieldLabel: t`Company Name`,
            errorText: t`*Required`,
            hasError: !(updatedAccount as AccountType)?.metadata?.company,
          },
          {
            fieldValue: (updatedAccount as AccountType)?.metadata?.username,
            fieldKey: 'username',
            fieldLabel: t`Username`,
            errorText: t`*Required`,
            hasError: !(updatedAccount as AccountType)?.metadata?.username,
          },
          {
            fieldValue: (updatedAccount as AccountType).metadata.secret,
            fieldKey: 'secret',
            fieldLabel: t`API Shared Secret`,
            isSecret: true,
            errorText: t`*Required`,
            hasError: !(updatedAccount as AccountType).metadata.secret,
            description: t`Secrets are securely stored and never surfaced to the user. Entering a new value will override the existing secret.`,
          },
        ]
      : [
          {
            fieldValue: (updatedAccount as AccountType).metadata.company,
            fieldKey: 'company',
            fieldLabel: t`Organization ID`,
            errorText: t`*Required`,
            hasError: !(updatedAccount as AccountType).metadata.company,
          },
          {
            fieldValue: (updatedAccount as AccountType).metadata.client_id,
            fieldKey: 'client_id',
            fieldLabel: t`Client ID`,
            errorText: t`*Required`,
            hasError: !(updatedAccount as AccountType).metadata.client_id,
            description: '',
          },
          {
            fieldValue: (updatedAccount as AccountType).metadata.client_secret,
            fieldKey: 'client_secret',
            fieldLabel: t`Client Secret`,
            errorText: t`*Required`,
            hasError: !(updatedAccount as AccountType).metadata.client_secret,
            description: '',
          },
          {
            fieldValue: (updatedAccount as AccountType).metadata.scopes,
            fieldKey: 'scopes',
            fieldLabel: t`Scopes`,
            errorText: t`*Required`,
            hasError: !(updatedAccount as AccountType).metadata.scopes,
            description: '',
          },
        ]

  const updateMetadataHostingType = (value: string) => {
    setUpdatedAccount(prevState => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        company: '',
        username: '',
        secret: '',
        client_id: '',
        client_secret: '',
        scopes: '',
        auth_type: value,
      },
    }))
  }

  const [OAUTH_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.OAUTH_ADOBE_CONNECTOR]),
  )

  return (
    <div>
      <FlexRadioInline
        value={(updatedAccount as AccountType).metadata?.auth_type ?? 'fields'}
        onChange={({ target: { value } }) => {
          updateMetadataHostingType(value)
        }}
      >
        <FormControlLabel value="fields" control={<Radio />} label={t`User/Password`} />
        {OAUTH_ENABLED && <FormControlLabel value="oauth" control={<Radio />} label={t`OAuth`} />}
      </FlexRadioInline>
      <AccountFieldManager
        newConnection={newConnection}
        updatedAccount={updatedAccount}
        setUpdatedAccount={setUpdatedAccount}
        accountFields={AEMFields}
        setRequiredFieldsMissing={setRequiredFieldMissing}
      />
    </div>
  )
}
